import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosObj } from "../../../config/apiClients";
import { DashboardStats } from '../../types';
import { CustomDateParams, StatDetailsQuery } from '../../../utils/types';

const initialState: DashboardStats = {
  stats: {},
  customStats: null,
  pendingRequestCounts: null,
  isLoading: false,
  error: null,
  statDetails: [],
  statDetailsPagination: null,
  isLoadingStatDetails: false,
  statDetailsError: null
};

// Thunk to get regular stats
export const getStats = createAsyncThunk(
  'home/getStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/home/getStats');
      return response.data.payload;
    } catch (error) {
      return rejectWithValue(error || 'Failed to fetch Dashboard Stats');
    }
  }
);

// Thunk to get custom date range stats
export const getCustomDateStats = createAsyncThunk(
  'home/getCustomDateStats',
  async (params: CustomDateParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(
        `/home/getStats?startDate=${params.startDate}&endDate=${params.endDate}`
      );
      return response.data.payload.custom_range;
    } catch (error) {
      return rejectWithValue(error || 'Failed to fetch Dashboard Custom Stats');
    }
  }
);

export const getPendingRequestCounts = createAsyncThunk(
  'home/pendingRequestsCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/home/pendingRequestsCounts');
      return response.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to fetch Pending Requests Count');
    }
  }
)

export const getStatDetails = createAsyncThunk(
  'home/getStatDetails',
  async (query: StatDetailsQuery, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/home/getStatDetails', { params: query });
      return response.data.payload;
    } catch (error) {
      return rejectWithValue(error || 'Failed to fetch stat details');
    }
  }
);

export const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    clearStatDetails: (state) => {
      state.statDetails = [];
      state.statDetailsPagination = null;
      state.statDetailsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(getStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as { message: string }).message;
      })
      .addCase(getCustomDateStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCustomDateStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customStats = action.payload;
      })
      .addCase(getCustomDateStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as { message: string }).message;
      })
      .addCase(getPendingRequestCounts.fulfilled, (state, action) => {
        state.pendingRequestCounts = action.payload.payload;
      })
      .addCase(getStatDetails.pending, (state) => {
        state.isLoadingStatDetails = true;
        state.statDetailsError = null;
      })
      .addCase(getStatDetails.fulfilled, (state, action) => {
        state.isLoadingStatDetails = false;
        state.statDetails = action.payload.records;
        state.statDetailsPagination = {
          totalItems: action.payload.totalItems,
          currentPage: action.payload.page,
          totalPages: action.payload.totalPages,
          hasNextPage: action.payload.page < action.payload.totalPages,
        };
      })
      .addCase(getStatDetails.rejected, (state, action) => {
        state.isLoadingStatDetails = false;
        state.statDetailsError = (action.payload as { message: string }).message;
      });
  }
});

export const { clearStatDetails } = homeSlice.actions;

export default homeSlice.reducer;